// src/screens/Login/Login.js
import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { signInWithEmailAndPassword, onAuthStateChanged, sendPasswordResetEmail } from "firebase/auth";
import { auth } from '../../firebase/firebase';
import './Login.css';

const Login = () => {
  const history = useHistory();
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Mensaje de feedback del reset
  const [resetMessage, setResetMessage] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  // Si ya estoy logueado, voy al home
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      if (u) history.push('/');
    });
    return () => unsub();
  }, [history]);

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
    // Si el usuario corrige datos, limpiamos mensajes
    setError('');
    setResetMessage('');
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setResetMessage('');
    try {
      const { user } = await signInWithEmailAndPassword(auth, form.email, form.password);

      // Sincronizo con localStorage para compatibilidad con tu lógica actual
      localStorage.setItem('user', JSON.stringify({
        email: user.email,
        uid: user.uid,
        name: user.displayName || ''
      }));
      localStorage.setItem('isLoggedIn', 'true');

      history.push('/');
    } catch (err) {
      // Mensajes más claros según código (cubrimos varios)
      const code = err?.code || '';
      if (code === 'auth/wrong-password') {
        setError("Email o contraseña incorrectos.");
      } else if (code === 'auth/user-not-found') {
        setError("No existe una cuenta con ese email.");
      } else if (code === 'auth/invalid-credential') {
        setError("Credenciales inválidas.");
      } else if (code === 'auth/too-many-requests') {
        setError("Demasiados intentos fallidos. Probá más tarde o recuperá tu contraseña.");
      } else if (code === 'auth/invalid-email') {
        setError("El email no tiene un formato válido.");
      } else if (code === 'auth/missing-password') {
        setError("Ingresá tu contraseña.");
      } else {
        setError("No se pudo iniciar sesión. Intentá de nuevo o recuperá tu contraseña.");
      }
    }
  };

  const handleResetPassword = async () => {
    setResetMessage('');
    const email = (form.email || '').trim();
    if (!email) {
      setResetMessage("Ingresá tu email arriba para enviarte el enlace de recuperación.");
      return;
    }
    try {
      setResetLoading(true);
      await sendPasswordResetEmail(auth, email);
      setResetMessage(`Te enviamos un email a ${email} para recuperar tu contraseña.`);
    } catch (err) {
      const code = err?.code || '';
      if (code === 'auth/user-not-found') {
        setResetMessage("No encontramos una cuenta con ese email.");
      } else if (code === 'auth/invalid-email') {
        setResetMessage("El email no tiene un formato válido.");
      } else {
        setResetMessage("No pudimos enviar el email de recuperación. Intentá más tarde.");
      }
    } finally {
      setResetLoading(false);
    }
  };

  // Iconos SVG reutilizables
  const EyeOpen = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path d="M12 5C7 5 2.73 8.11 1 12c1.73 3.89 6 7 11 7s9.27-3.11 11-7c-1.73-3.89-6-7-11-7Zm0 12a5 5 0 1 1 0-10 5 5 0 0 1 0 10Z"/>
      <circle cx="12" cy="12" r="2.5"/>
    </svg>
  );
  const EyeClosed = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path d="M2 4.27 3.28 3 21 20.72 19.73 22l-3.02-3.02C15.41 19.63 13.77 20 12 20c-5 0-9.27-3.11-11-7 1.02-2.3 2.86-4.26 5.13-5.58L2 4.27ZM12 8a4 4 0 0 1 4 4c0 .55-.11 1.08-.3 1.56L9.44 7.3C9.92 7.11 10.45 7 11 7h1Zm10 4c-.54 1.21-1.34 2.33-2.34 3.28l-1.43-1.43C19.18 13 19.64 12.52 20 12c-1.73-3.89-6-7-11-7-.86 0-1.7.09-2.5.27L5.82 4.59C7.68 3.86 9.78 3.5 12 3.5c5 0 9.27 3.11 11 7Z"/>
    </svg>
  );

  return (
    <div className="login-container">
      <h1 className="login-title">Iniciar Sesión</h1>
      {error && <p className="auth-error" role="alert">{error}</p>}

      <form onSubmit={handleSubmit} className="login-form">
        <label>Email
          <input
            name="email"
            type="email"
            value={form.email}
            onChange={handleChange}
            autoComplete="email"
          />
        </label>

        <label>Contraseña
          <div className="input-with-toggle">
            <input
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={form.password}
              onChange={handleChange}
              autoComplete="current-password"
            />
            <button
              type="button"
              className="toggle-eye"
              onClick={() => setShowPassword(v => !v)}
              aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
              title={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            >
              {showPassword ? <EyeOpen /> : <EyeClosed />}
            </button>
          </div>
        </label>

        {/* CTA de recuperación debajo de los campos: aparece cuando hay error */}
        {Boolean(error) && (
          <div className="reset-cta">
            ¿Te olvidaste la contraseña?{' '}
            <button
              type="button"
              className="linklike"
              onClick={handleResetPassword}
              disabled={resetLoading}
            >
              ¡Recuperala acá!
            </button>
            {resetMessage && <p className="auth-info">{resetMessage}</p>}
          </div>
        )}

        <button type="submit" className="login-button">Entrar</button>
      </form>

      <p className="login-switch">
        ¿No tenés cuenta?{' '}
        <span onClick={() => history.push('/register')} className="auth-link">
          Registrate aquí
        </span>
      </p>
    </div>
  );
};

export default Login;
